import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { ArrowRight, Award, Clock, Users } from 'lucide-react'
import { ButtonLink } from '@/components/generals/button-link'
import { Container } from '@/components/generals/container'
import { WindowFrame } from '@/components/generals/window-frame'

interface TerminalLine {
    kind: 'input' | 'output'
    prompt?: string
    text: string
}

const HIGHLIGHTS = [
    { icon: Users, label: "Up to 12 students a class" },
    { icon: Clock, label: "Morning, evening and weekend batches" },
    { icon: Award, label: "Certificate when you finish" }
]

const TERMINAL_LINES: Array<TerminalLine> = [
    { kind: 'input', prompt: '$', text: "python3 budget.py" },
    { kind: 'output', text: "Reading expenses.csv..." },
    { kind: 'output', text: "Total this month: 1,284" },
    { kind: 'input', prompt: '>>>', text: `print("I can code!")` },
    { kind: 'output', text: "I can code!" }
]

const SHEET_ROWS = [
    { label: "Rent", value: '650' },
    { label: "Food", value: '320' },
    { label: "Travel", value: '140' },
    { label: "Bills", value: '174' }
]

const HeroVisual: FC = (): ReactNode => {
    return (
        <div className='relative mx-auto w-full max-w-xl pb-20 animate-in fade-in slide-in-from-bottom-8 duration-1000 motion-reduce:animate-none sm:pb-24 lg:max-w-none'>
            <WindowFrame
                title='live-class / room-2'
                actions={(
                    <span className='inline-flex items-center gap-1.5 rounded-full bg-red-500/10 px-2 py-0.5 font-mono text-[10px] font-semibold text-red-600'>
                        <span className='size-1.5 rounded-full bg-red-500' aria-hidden='true' />
                        LIVE
                    </span>
                )}
            >
                <div className='relative aspect-[4/3] sm:aspect-[5/4]'>
                    <Image
                        src='/images/hero.jpg'
                        alt="Students talking through an exercise together during a class"
                        fill
                        preload
                        sizes='(min-width: 1280px) 580px, (min-width: 640px) 576px, 100vw'
                        className='object-cover object-[center_40%]'
                    />
                </div>
            </WindowFrame>

            <div aria-hidden='true' className='absolute top-6 -left-10 hidden w-48 rounded-2xl border bg-card p-3 shadow-2xl shadow-foreground/10 animate-float motion-reduce:animate-none xl:block'>
                <div className='flex items-center gap-2 rounded-lg border bg-muted/70 px-2.5 py-1.5 font-mono text-[11px]'>
                    <span className='font-semibold text-primary'>fx</span>
                    <span className='text-muted-foreground'>=SUM(B2:B5)</span>
                </div>

                <div className='mt-2.5 overflow-hidden rounded-lg border font-mono text-[11px]'>
                    {SHEET_ROWS.map(row => (
                        <div key={row.label} className='grid grid-cols-[1fr_auto] border-b px-2.5 py-1.5'>
                            <span className='text-muted-foreground'>{row.label}</span>
                            <span className='tabular-nums'>{row.value}</span>
                        </div>
                    ))}

                    <div className='grid grid-cols-[1fr_auto] bg-accent px-2.5 py-1.5 font-semibold text-accent-foreground'>
                        <span>Total</span>
                        <span className='tabular-nums'>1,284</span>
                    </div>
                </div>
            </div>

            <div aria-hidden='true' className='absolute bottom-0 left-0 w-[88%] sm:w-[58%] lg:-left-10 lg:w-[62%] xl:w-[60%]'>
                <WindowFrame tone='dark' title='python3 / lesson-06'>
                    <div className='space-y-1 p-4 font-mono text-[11px] leading-relaxed sm:text-xs'>
                        {TERMINAL_LINES.map(line => (
                            <p key={line.text} className={line.kind === 'output' ? 'text-background/60' : 'text-background'}>
                                {line.prompt && (
                                    <span className='mr-2 text-accent'>{line.prompt}</span>
                                )}
                                {line.text}
                            </p>
                        ))}

                        <p>
                            <span className='mr-2 text-accent'>{'>>>'}</span>
                            <span className='inline-block h-3.5 w-2 translate-y-0.5 bg-accent animate-caret motion-reduce:animate-none' />
                        </p>
                    </div>
                </WindowFrame>
            </div>

            <div aria-hidden='true' className='absolute right-0 bottom-3 hidden items-center gap-3 rounded-2xl border bg-card px-4 py-3 shadow-2xl shadow-foreground/10 sm:flex lg:hidden xl:flex'>
                <span className='flex size-10 items-center justify-center rounded-xl bg-accent text-accent-foreground'>
                    <Award className='size-5' />
                </span>

                <span>
                    <span className='block text-sm font-semibold'>Certificate earned</span>
                    <span className='block text-xs text-muted-foreground'>Final project passed</span>
                </span>
            </div>
        </div>
    )
}

export const HeroSection: FC = (): ReactNode => {
    return (
        <section id='hero' aria-labelledby='hero-title' className='relative overflow-hidden'>
            <div
                aria-hidden='true'
                className='bg-dots pointer-events-none absolute inset-0 text-foreground/40 [mask-image:radial-gradient(ellipse_80%_60%_at_50%_0%,black,transparent)]'
            />
            <div aria-hidden='true' className='pointer-events-none absolute -top-48 -right-40 size-[38rem] rounded-full bg-primary/10 blur-3xl' />
            <div aria-hidden='true' className='pointer-events-none absolute -bottom-48 -left-40 size-[30rem] rounded-full bg-accent/25 blur-3xl' />

            <Container className='relative grid items-center gap-14 pt-12 pb-16 sm:pt-16 sm:pb-20 lg:grid-cols-[1.05fr_1fr] lg:gap-12 lg:pt-20 lg:pb-24'>
                <div className='animate-in fade-in slide-in-from-bottom-4 duration-700 motion-reduce:animate-none'>
                    <p className='inline-flex items-center gap-2 rounded-full border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-sm'>
                        <span className='relative flex size-2' aria-hidden='true'>
                            <span className='absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-75 motion-reduce:animate-none' />
                            <span className='relative inline-flex size-2 rounded-full bg-emerald-500' />
                        </span>
                        Enrollment open for the next batches
                    </p>

                    <h1 id='hero-title' className='mt-6 text-[2.75rem] leading-[1.02] font-semibold sm:text-6xl lg:text-[4.25rem]'>
                        From your first click to{' '}
                        <span className='relative isolate inline-block'>
                            <span
                                aria-hidden='true'
                                className='absolute inset-x-[-0.12em] bottom-[0.06em] -z-10 h-[0.4em] -rotate-1 rounded-[0.15em] bg-accent'
                            />
                            job-ready
                        </span>
                        {' '}skills
                        <span
                            aria-hidden='true'
                            className='ml-2 inline-block h-[0.78em] w-[0.36em] translate-y-[0.06em] rounded-[3px] bg-primary animate-caret motion-reduce:animate-none'
                        />
                    </h1>

                    <p className='mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground sm:text-xl'>
                        Hands-on computer courses for complete beginners, students and working professionals. Learn Office, Excel, design and coding in small classes, with a patient instructor right beside you.
                    </p>

                    <div className='mt-9 flex flex-col gap-3 sm:flex-row'>
                        <ButtonLink href='/courses' size='lg'>
                            Browse courses
                            <ArrowRight aria-hidden='true' />
                        </ButtonLink>

                        <ButtonLink href='/enroll' size='lg' variant='outline'>
                            Enroll now
                        </ButtonLink>
                    </div>

                    <ul className='mt-10 grid gap-3 text-sm text-muted-foreground sm:grid-cols-3 sm:gap-4'>
                        {HIGHLIGHTS.map(({ icon: Icon, label }) => (
                            <li key={label} className='flex items-center gap-2.5'>
                                <span className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary'>
                                    <Icon className='size-4' aria-hidden='true' />
                                </span>
                                {label}
                            </li>
                        ))}
                    </ul>
                </div>

                <HeroVisual />
            </Container>
        </section>
    )
}
